import React, { ReactNode } from 'react'
import { Entypo, EvilIcons, Feather, FontAwesome, Ionicons } from '@expo/vector-icons'
import { translate } from '@vatom/utils'
import { PressableOpacity, Status, Text, Toast } from '@vatom/wombo'
import * as Clipboard from 'expo-clipboard'
import { Actionsheet, Box, Flex, Icon, Link, ScrollView } from 'native-base'

import { useMaxWith } from '../../../components/WebWrapper'
import ActionSheetHeader, { HeaderType } from '../partials/ActionSheetHeader'

interface ActionSheetItemProps {
  label?: ReactNode
  subLabel?: ReactNode
  color?: string
}

export const Item = ({ label, subLabel, color }: ActionSheetItemProps) => {
  return (
    <Flex direction="column">
      <Box>
        <Text fontFamily="Inter-SemiBold" fontSize="15px" color={color || 'normalText'}>
          {label}
        </Text>
      </Box>
      <Box>
        <Text fontFamily="Inter-Regular" fontSize="11px" color={color || 'lightText'}>
          {subLabel}
        </Text>
      </Box>
    </Flex>
  )
}

interface ActionSheetShareProps {
  setOpen: (b: boolean) => void
  isOpen: boolean
  shareLink: string
  tokenName: string
}

export const ActionSheetShare = ({
  isOpen,
  setOpen,
  shareLink,
  tokenName
}: ActionSheetShareProps) => {
  const maxWidth = useMaxWith()
  const shareOptions = [
    {
      name: 'sc-facebook',
      iconType: EvilIcons,
      iconColor: 'white',
      boxBGColor: 'facebook.500',
      label: translate('shareNft.shareFb'),
      subLabel: translate('shareNft.openFb'),
      link: `http://www.facebook.com/share.php?u=${shareLink}?utm_source=facebook&utm_medium=share&login=1`
    },
    {
      name: 'twitter',
      iconType: FontAwesome,
      iconColor: 'white',
      boxBGColor: 'twitter.500',
      label: translate('shareNft.shareTwitter'),
      subLabel: translate('shareNft.openTwitter'),
      link: `https://twitter.com/intent/tweet?${shareLink}?utm_source=twitter&utm_medium=share&login=1`
    },
    {
      name: 'message-circle',
      iconType: Feather,
      iconColor: 'white',
      boxBGColor: 'green.500',
      label: translate('shareNft.shareSms'),
      subLabel: translate('shareNft.openSMS'),
      link: `sms:?&body=${shareLink}?utm_source=sms&utm_medium=share&login=1`
    },
    {
      name: 'mail',
      iconType: Entypo,
      iconColor: 'white',
      boxBGColor: 'lightBlue.500',
      label: translate('shareNft.shareEmail'),
      subLabel: translate('shareNft.openEmail'),
      link: `mailto:?subject=${tokenName}&${shareLink}?utm_source=email&utm_medium=share&login=1`
    },
    {
      name: 'link',
      iconType: Ionicons,
      iconColor: 'lightText',
      boxBGColor: 'extraLightText',
      label: translate('shareNft.copyLink'),
      subLabel: translate('shareNft.copySubHeader'),
      link: `${shareLink}?utm_source=link&utm_medium=share&login=1`
    }
  ]
  return (
    <Actionsheet
      isOpen={isOpen}
      onClose={() => setOpen(false)}
      maxWidth={maxWidth}
      margin={'auto'}
      useRNModal={true}
      // @ts-ignore prop doesn't exit on types
      _backdropFade={{
        entryDelay: 150,
        exitDelay: 10
      }}
    >
      <Actionsheet.Content pt="20px" pb="16px" px={0}>
        <ScrollView w="100%">
          <Box px="16px" w="100%">
            <ActionSheetHeader
              type={HeaderType.close}
              hasText={false}
              onPress={() => setOpen(false)}
              title={translate('common.share')}
            />
          </Box>

          <Text
            fontSize={15}
            fontWeight="400"
            textAlign="center"
            marginBottom={14}
            marginTop={27}
            color="darkText"
            tx="shareNft.selectOptions"
          />
          {shareOptions.map((item, index) => {
            return (
              <Actionsheet.Item
                key={`share2-${index}`}
                startIcon={
                  <Box
                    size="48px"
                    borderRadius="full"
                    alignItems="center"
                    justifyContent="center"
                    bg={item.boxBGColor}
                  >
                    <Icon as={item.iconType} size="25px" name={item.name} color={item.iconColor} />
                  </Box>
                }
                _hover={{
                  bg: 'gray.100:alpha.20'
                }}
                _pressed={{
                  bg: 'gray.100:alpha.20'
                }}
              >
                {item.name === 'link' ? (
                  <PressableOpacity
                    onPress={() => {
                      Clipboard.setStringAsync(item.link)
                      Toast({
                        title: `Copied to Clipboard - ${item.link}`,
                        placement: 'top',
                        status: Status.success
                      })
                    }}
                  >
                    <Item label={item.label} subLabel={item.subLabel} />
                  </PressableOpacity>
                ) : (
                  <Link
                    href={item.link}
                    isExternal={true}
                    _text={{
                      color: 'blue.400',
                      fontSize: '11px'
                    }}
                    mt={-0.5}
                    _web={{
                      mb: -2
                    }}
                  >
                    <Item label={item.label} subLabel={item.subLabel} />
                  </Link>
                )}
              </Actionsheet.Item>
            )
          })}
        </ScrollView>
      </Actionsheet.Content>
    </Actionsheet>
  )
}
export default ActionSheetShare
